import { SvgDropdown, SvgError } from '@chilipiper/icons/src/design-system'
import React from 'react'
import styled from 'styled-components'
import { Icon } from '../../../../old/icon'
import { Flex, Text } from '../../../core-components'
import {
  UnstyledButton,
  Props as UnstyledButtonProps,
} from '../../../core-components/unstyled-button/UnstyledButton'
import { StartContentIcon } from '../../data/start-content/StartContent'
import { EndContentProvider } from '../../data'
import { IconTooltip } from '../../feedback'

export const SelectedText = styled(Text)``
const DropdownIcon = styled(Icon)``

type Variant = 'regular' | 'nested' | 'ghost'

interface Props extends UnstyledButtonProps {
  hasError: boolean
  isOpen: boolean
  variant: Variant
}

export const TriggerStartIcon = styled(Icon)``

export const StyledSelectTrigger = styled(UnstyledButton)<Props>`
  background: ${({ theme, isOpen, hasError, variant }) =>
    theme.colors[getSelectBg(isOpen, hasError, variant)]};
  box-shadow: ${({ theme, variant, isOpen, hasError }) =>
    theme.shadows[getSelectShadow(isOpen, hasError, variant)]};

  :hover,
  :focus-visible {
    background: ${({ theme, isOpen }) =>
      isOpen ? undefined : theme.colors['bg/form-item-hovered']};
  }

  &[disabled] {
    background: ${({ theme }) => theme.colors['bg/disabled']};

    ${SelectedText}, ${TriggerStartIcon}, ${DropdownIcon}, ${StartContentIcon} {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }
  }

  :hover,
  &[disabled] {
    box-shadow: ${({ isOpen }) => (isOpen ? undefined : 'none')};
  }

  :focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
  }
`

export const BaseSelectEndContent = ({
  endContent,
  errorMessage,
  variant,
}: {
  endContent: React.ReactNode
  errorMessage?: string
  variant: Variant
}) => (
  <Flex alignItems='center' gap={2}>
    <EndContentProvider value={{ iconColor: 'fg/minor' }}>{endContent}</EndContentProvider>
    {errorMessage && (
      <>
        {variant === 'nested' ? (
          <IconTooltip variant='error' content={errorMessage} size={4} />
        ) : (
          <Icon icon={SvgError} color='fg/caution' w={4} h={4} />
        )}
      </>
    )}
    <DropdownIcon icon={SvgDropdown} color='fg/minor' w={4} h={4} ml={1} />
  </Flex>
)

const getSelectBg = (isOpen: boolean, hasError: boolean, variant: Variant) => {
  if (isOpen) {
    return 'bg/form-item-focused'
  }
  if (hasError) {
    return 'bg/form-item-error'
  }
  if (variant === 'regular') {
    return 'bg/form-item-default'
  }
  return 'transparent'
}

const getSelectShadow = (isOpen: boolean, hasError: boolean, variant: Variant) => {
  if (isOpen) {
    if (variant === 'regular') {
      return 'border/form-item-focused'
    }
    return 'border/form-item-nested-focused'
  }
  if (hasError) {
    return 'none'
  }
  if (variant === 'regular') {
    return 'border/section'
  }
  return 'none'
}
